<script>
import cloneDeep from 'lodash/cloneDeep';
import PhotoUploader from '@components/uploaders/photo-uploader.vue';
import { mask } from 'vue-the-mask';
import {
  adminPanelComputed,
  adminPanelMethods,
  exerciseComputed,
  exerciseMethods,
  mediaComputed,
  mediaMethods,
} from '@state/helpers';

export default {
  components: {
    PhotoUploader,
  },
  directives: { mask },
  props: {
    content: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      stepObj: {},
    };
  },
  computed: {
    ...adminPanelComputed,
    ...exerciseComputed,
    ...mediaComputed,
    coverPhotoUrl() {
      return this.stepObj.mediaAssets &&
        this.stepObj.mediaAssets.coverPhoto &&
        this.stepObj.mediaAssets.coverPhoto.url
        ? this.stepObj.mediaAssets.coverPhoto.url
        : null;
    },
  },
  mounted() {},
  methods: {
    ...adminPanelMethods,
    ...exerciseMethods,
    ...mediaMethods,
    clearForm() {
      this.stepObj = {};
      const hasPanelDetail = this.panelContent.data.componentRight;
      if (hasPanelDetail && hasPanelDetail.showPanelDetail) {
        this.setHidePanelContentDetail();
      } else {
        this.setHideAdminPanel();
      }
    },
    handlePanelVisibility() {
      const hasPanelDetail = this.panelContent.data.componentRight;
      if (hasPanelDetail !== undefined) {
        this.setHidePanelContentDetail();
      } else {
        this.setHideAdminPanel();
      }
    },
    assignMediaUrls() {
      this.stepObj.mediaAssets = {
        coverPhoto: {
          url: this.hasNewMedia('cover', 'exerciseSteps')
            ? this.hasNewMedia('cover', 'exerciseSteps').publicUrl
            : null,
        },
      };
    },
    async buildStep() {
      this.stepObj.order = this.selectedExercise.steps.length + 1;
      await this.assignMediaUrls();
      return this.stepObj;
    },
    async create() {
      const step = await this.buildStep();
      this.setAddStep(step);
      this.clearForm();
      this.handlePanelVisibility();
    },
    async update() {
      const stepIndex =
        // use nullish (?? - null/undefined) operator as 0 is an okay value to have
        this.panelContent.data.index ??
        this.panelContent.data.componentRight.exerciseStep.index;
      await this.assignMediaUrls();
      this.setUpdateStep({ step: this.stepObj, index: stepIndex });
      this.clearForm();
      this.handlePanelVisibility();
      this.setSelectedStep(null);
    },
    async onSave() {
      if (Object.keys(this.stepObj).length) {
        this.selectedStep ? this.update() : this.create();
      }
    },
  },
  watch: {
    isAdminPanelActive(newVal, preVal) {
      if (
        newVal &&
        this.panelContent &&
        this.panelContent.data &&
        this.panelContent.data.step &&
        this.panelContent.data.step.order
      ) {
        this.stepObj = cloneDeep(this.selectedStep.step);
      }
    },
    panelContent(newVal, preVal) {
      if (
        newVal &&
        this.panelContent &&
        this.panelContent.data &&
        this.panelContent.data.componentRight &&
        this.panelContent.data.componentRight.exerciseStep &&
        this.panelContent.data.componentRight.exerciseStep.step &&
        this.panelContent.data.componentRight.exerciseStep.step.order
      ) {
        this.stepObj = cloneDeep(this.selectedStep.step);
      }
    },
  },
};
</script>

<template>
  <form class="exercise-step-form" @submit.prevent="onSave">
    <div class="row">
      <div class="col">
        <label>Title</label>
        <BaseInputText name="title" v-model="stepObj.title" />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <label>Description</label>
        <BaseInputText
          type="textarea"
          name="description"
          v-model="stepObj.description"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-3">
        <label>Duration</label>
        <BaseInputText
          type="tel"
          name="duration"
          v-model="stepObj.duration"
          v-mask="'##:##'"
          placeholder="mm:ss"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <label>Preview Image From Video</label>
        <div class="helper-text">
          The photo will be used primarily on the admin side, not the user
          interface.
        </div>

        <PhotoUploader
          requestType="exerciseSteps"
          helper-text="Photos must be .jpg, .jpeg, .gif, or .webp, no larger than 10MB."
        />
      </div>
    </div>

    <div class="panel-footer">
      <BaseButton
        type="button"
        class="btn-primary btn-clear"
        @click="clearForm"
      >
        <BaseIcon name="times" />
        Cancel Changes
      </BaseButton>
      <span class="divider-vertical">&nbsp;</span>
      <BaseButton type="button" class="btn-primary btn-clear" @click="onSave">
        <BaseIcon name="save" />
        Save Changes
      </BaseButton>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@import '@design';

.exercise-step-form {
  .panel-footer {
    background-color: $color-header-bg;
    border-radius: $size-input-border-radius;
    bottom: 1rem;
    display: flex;
    justify-content: space-around;
    padding: 1rem 2rem;
    position: fixed;
    width: 94%;
    z-index: 9999;

    &.form-error {
      background-color: $color-error-dark;
    }

    .divider-vertical {
      border-left: 1px solid white;
      height: 2.5rem;
    }

    .btn-primary {
      &:active {
        background: rgba($color-white-wo-rgb, 0.1);
        color: white;
      }

      &:focus {
        background: transparent;
        color: white;
      }

      &:not(:disabled):not(.disabled) {
        &:active {
          background: rgba($color-white-wo-rgb, 0.1);
          color: white;
        }

        &:focus {
          background: transparent;
          color: white;
        }
      }
    }

    .btn-clear {
      color: white;
    }
  }
}
</style>
